import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, GripVertical, RotateCcw, Columns3 } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";

export function ReportColumnsPanel<T extends string>({
  columnOrder,
  hiddenColumns,
  requiredColumns,
  labelFor,
  onToggle,
  onMove,
  onReorder,
  onReset,
  testIdPrefix,
}: {
  columnOrder: readonly T[];
  hiddenColumns: ReadonlySet<T>;
  requiredColumns: ReadonlySet<T>;
  labelFor: (id: T) => string;
  onToggle: (id: T) => void;
  onMove: (id: T, direction: "up" | "down") => void;
  onReorder: (fromId: T, toId: T) => void;
  onReset: () => void;
  testIdPrefix: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [draggingId, setDraggingId] = useState<T | null>(null);
  const [dropId, setDropId] = useState<T | null>(null);

  return (
    <div className="grid gap-1" data-testid={`${testIdPrefix}-columns-panel`}>
      {columnOrder.map((columnId, index) => {
        const required = requiredColumns.has(columnId);
        const visible = !hiddenColumns.has(columnId);
        const label = labelFor(columnId);
        return (
          <div
            key={columnId}
            className={cn(
              "flex items-center gap-1 rounded-md",
              dropId === columnId && draggingId != null && draggingId !== columnId
                ? "bg-accent/10"
                : null,
            )}
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setDropId(columnId);
            }}
            onDrop={(event) => {
              event.preventDefault();
              const fromRaw = event.dataTransfer.getData("text/plain") as T;
              const from = columnOrder.includes(fromRaw) ? fromRaw : draggingId;
              setDraggingId(null);
              setDropId(null);
              if (from) onReorder(from, columnId);
            }}
          >
            <span
              draggable
              className={cn(
                "inline-grid size-7 shrink-0 select-none place-items-center text-muted",
                draggingId === columnId ? "cursor-grabbing" : "cursor-grab",
              )}
              aria-label={t("reports.chrome.reorderColumn", { column: label })}
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", columnId);
                setDraggingId(columnId);
              }}
              onDragEnd={() => {
                setDraggingId(null);
                setDropId(null);
              }}
            >
              <GripVertical size={14} aria-hidden="true" />
            </span>
            <label className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-[0.8125rem]">
              <input
                type="checkbox"
                checked={visible}
                disabled={required && visible}
                onChange={() => onToggle(columnId)}
                className="size-4 accent-[var(--color-accent)]"
                data-testid={`${testIdPrefix}-column-toggle-${columnId}`}
              />
              <span className="min-w-0 truncate">{label}</span>
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 p-0"
              disabled={index === 0}
              aria-label={t("reports.chrome.moveColumnUp", { column: label })}
              onClick={() => onMove(columnId, "up")}
            >
              <ArrowUp size={14} aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 p-0"
              disabled={index === columnOrder.length - 1}
              aria-label={t("reports.chrome.moveColumnDown", { column: label })}
              onClick={() => onMove(columnId, "down")}
            >
              <ArrowDown size={14} aria-hidden="true" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 h-8 justify-start"
        onClick={onReset}
        data-testid={`${testIdPrefix}-reset-columns`}
      >
        <RotateCcw size={14} aria-hidden="true" />
        {t("reports.chrome.resetColumns")}
      </Button>
    </div>
  );
}

export function ReportColumnsPopover<T extends string>(
  props: Parameters<typeof ReportColumnsPanel<T>>[0],
): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-[0.75rem]"
          data-testid={`${props.testIdPrefix}-columns`}
        >
          <Columns3 size={14} aria-hidden="true" />
          {t("reports.chrome.columns")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <ReportColumnsPanel {...props} />
      </PopoverContent>
    </Popover>
  );
}
